// src/components/resources/quizzes/components/SectionReview.tsx

'use client'

import { useEffect, useMemo } from 'react'
import { clientLogger } from '~/lib/logger-client'
import { cn } from '~/lib/utils'
import { formatPercent } from '~/lib/utils/formatters'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import type { QuizSection, QuizQuestion } from '~/lib/resources/types/tools/quiz'
import { isAnswerCorrect } from '~/components/resources/quizzes/utils/answer-correctness'

interface SectionReviewProps {
  section: QuizSection
  answers: Record<string, string | string[]>
}

export function SectionReview({ section, answers }: SectionReviewProps): JSX.Element {
  const getOptionText = (answer: string | string[], question: QuizQuestion): string => {
    // For text questions, return directly
    if (question.type === 'text') {
      return String(answer)
    }

    // For number questions, format the number
    if (question.type === 'number') {
      return String(Number(answer))
    }

    // For multiselect questions
    if (question.type === 'multiselect' && Array.isArray(answer)) {
      return answer.map(id => {
        const option = question.options.find(opt => opt.id === id)
        if (!option) {
          return id
        }
        return option.label
      }).join(', ')
    }

    // For ordered questions
    if (question.type === 'ordered' && Array.isArray(answer)) {
      return answer.map((id, index) => {
        const option = question.options.find(opt => opt.id === id)
        if (!option) {
          return id
        }
        return `${index + 1}. ${option.label}`
      }).join('\n')
    }

    // For single select and visual questions
    const option = question.options.find(opt => opt.id === String(answer))
    if (!option) {
      return String(answer)
    }
    return option.label
  }

  // Detect saved-answer/option drift (an answer referencing an option no longer
  // present on its question) during render as a PURE pass, then log it from the
  // effect below. getOptionText runs inline while rendering each answer row, so
  // logging there would POST to /api/log on every re-render / StrictMode retry.
  const missingOptionWarnings = useMemo(() => {
    const out: string[] = []
    for (const question of section.questions) {
      if (question.type === 'text' || question.type === 'number') continue
      const answer = answers[question.id]
      if (answer == null) continue
      const ids = Array.isArray(answer) ? answer : [String(answer)]
      for (const id of ids) {
        if (!question.options.some(opt => opt.id === id)) {
          out.push(`Option ${id} not found in question ${question.id}`)
        }
      }
    }
    return out
  }, [section, answers])

  useEffect(() => {
    for (const message of missingOptionWarnings) {
      clientLogger.error(message, { component: 'SectionReview' })
    }
  }, [missingOptionWarnings])

  const correctAnswers = section.questions.filter(question => {
    const answer = answers[question.id]
    if (!answer) return false
    return isAnswerCorrect(question, answer)
  }).length

  const score = (correctAnswers / section.questions.length)

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Section Review: {section.title}
        </h2>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
          You scored {formatPercent(score)} ({correctAnswers} of {section.questions.length} correct)
        </p>
      </div>

      <div className="divide-y divide-gray-200 rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5 dark:divide-gray-800 dark:bg-gray-900 dark:ring-white/5">
        {section.questions.map((question, index) => {
          const answer = answers[question.id]
          const correct = answer ? isAnswerCorrect(question, answer) : false

          return (
            <div key={question.id} className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-900 dark:bg-gray-800 dark:text-white">
                      {index + 1}
                    </span>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">
                      {question.label}
                    </h3>
                  </div>

                  <div className="mt-4 pl-9">
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Your Answer:
                      </div>
                      <div className={cn(
                        "flex items-center gap-2 rounded-lg border px-3 py-2",
                        correct
                          ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-400"
                          : "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400"
                      )}>
                        {answer ? getOptionText(answer, question) : 'No answer provided'}
                        {correct ? (
                          <RiCheckLine className="ml-auto h-4 w-4" />
                        ) : (
                          <RiCloseLine className="ml-auto h-4 w-4" />
                        )}
                      </div>
                      {!correct && (
                        <>
                          <div className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Correct Answer:
                          </div>
                          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-700 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-400">
                            {getOptionText(question.correctAnswer, question)}
                            <RiCheckLine className="ml-auto h-4 w-4" />
                          </div>
                        </>
                      )}
                    </div>

                    {question.explanation && (
                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800/50">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {correct ? question.explanation.correct : question.explanation.incorrect[answer as string] ?? question.explanation.incorrect.default}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className={cn(
                  "flex h-6 w-6 flex-none items-center justify-center rounded-full",
                  correct
                    ? "bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400"
                    : "bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400"
                )}>
                  {correct ? (
                    <RiCheckLine className="h-4 w-4" />
                  ) : (
                    <RiCloseLine className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}